import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DOCUMENT, ElementRef, OnDestroy, OnInit, Renderer2, computed, effect, inject, input, output, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FilterSelection } from 'src/app/components/filters/filters.component';
import { CategoryLayoutType, DynamicPage } from 'src/app/core/enums/dynamic-page.enum';
import { CategoryLayoutData } from 'src/app/core/interfaces/store-configuration.interface';
import { DropdownItem } from 'src/app/core/models/dropdown.model';
import { DomMutationObserverService } from 'src/app/core/services/dom-mutation-observer';
import { OverlayService } from 'src/app/core/services/overlay/overlay.service';
import { createDefaultCategoryLayoutData } from 'src/app/core/utilities/type-defaults.util';
import { MappedFilter } from 'src/app/products/services/filters-adapter.service';
import { LinkHandler } from 'src/app/shared/class/link-handler.class';
import { BannerType, MOBILE_GRID_LAYOUT, PaginationType } from '../../enum/category-page.enum';
import { GridViewLayout, SortingLayout1Component } from '../sorting-layout-1/sorting-layout-1.component';

import { ProductListingHit } from 'src/app/products/models/product-listing.model';

import { ListingLoaderComponent } from "src/app/components/listing-loader/listing-loader.component";
import { BrowserApiService } from 'src/app/core/services/browser-api/browser-api.service';
import { DeviceDetectionService } from 'src/app/core/services/device-detection/device-detection.service';
import { CategoryFilterLayout2Component } from "../category-filter-layout-2/category-filter-layout-2.component";
import { CategoryPageShimmerComponent } from "../category-page-shimmer/category-page-shimmer.component";
import { ListingEmptyStateComponent } from "../listing-empty-state/listing-empty-state.component";
import { PaginationLayout1Component } from "../pagination-layout-1/pagination-layout-1.component";
import { ProductCardRectangularComponent } from "../product-card-rectangular/product-card-rectangular.component";
import { ProductCardSquareComponent } from "../product-card-square/product-card-square.component";

@Component({
  standalone: true,
  selector: 'app-category-page-layout-2',
  templateUrl: './category-page-layout-2.component.html',
  styleUrls: ['./category-page-layout-2.component.scss'],
  imports: [CommonModule, RouterModule, CategoryFilterLayout2Component, SortingLayout1Component, ProductCardSquareComponent, ProductCardRectangularComponent, PaginationLayout1Component, ListingLoaderComponent, ListingEmptyStateComponent, CategoryPageShimmerComponent]
})
export class CategoryPageLayout2Component extends LinkHandler implements OnInit, AfterViewInit, OnDestroy {
  public override renderer = inject(Renderer2);
  public override elementRef = inject(ElementRef);
  public override router = inject(Router);
  public override overlayService = inject(OverlayService);
  public override browserApiService = inject(BrowserApiService);
  private document = inject<Document>(DOCUMENT);
  private readonly deviceDetectionService = inject(DeviceDetectionService);
  private readonly domMutationObserver = inject(DomMutationObserverService);

  BannerType = BannerType;



  readonly category = input<string>('');
  readonly availableFilterAttributes = input<MappedFilter[]>([]);
  readonly currentAppliedFilters = input<FilterSelection[]>([]);
  readonly products = input<ProductListingHit[]>([]);
  readonly totalProductsCount = input<number>(0);
  readonly fetchedProductsCount = input<number>(0);
  readonly pageLoading = input<boolean>(false);
  readonly productLoading = input<boolean>(false);
  readonly listLoading = input<boolean>(false);
  readonly paginationOptions = input<DropdownItem<number>[] | null>(null);
  readonly paginationType = input<PaginationType>(PaginationType.PAGE);
  readonly currentPage = input<number>(1);
  readonly sortOptions = input<DropdownItem<string>[]>([]);
  readonly categoryLayoutData = input<CategoryLayoutData>(createDefaultCategoryLayoutData());
  readonly sortBy = input<string>('');
  readonly sortByChange$ = output<string>();
  readonly selectedGridViewLayout = input<string>("");
  readonly selectedGridViewLayoutChange$ = output<string>();
  override readonly page = input<DynamicPage>(DynamicPage.CATEGORY);
  readonly searchValue = input<string>("");
  readonly selectedMobileGridLayout = input<MOBILE_GRID_LAYOUT>(MOBILE_GRID_LAYOUT.GRID_LAYOUT1);

  // Internal signals for component state
  private _selectedGridViewLayout = signal<string>("");
  private _categoryLayoutData = signal<CategoryLayoutData>(createDefaultCategoryLayoutData());

  // Computed signal that returns the current grid view layout
  readonly currentGridViewLayout = computed(() => {
    const internalValue = this._selectedGridViewLayout();
    return internalValue || GridViewLayout.GRID_VIEW_LAYOUT4;
  });


  // Expose device detection service signals for template usage
  readonly isMobile = this.deviceDetectionService.isMobile;
  readonly isDesktop = this.deviceDetectionService.isDesktop;
  readonly isTablet = this.deviceDetectionService.isTablet;
  readonly deviceType = this.deviceDetectionService.deviceType;

  PaginationType = PaginationType;
  gridViewLayout = GridViewLayout;
  mobileFilterOpened = false;
  DynamicPage = DynamicPage;
  CategoryLayoutType = CategoryLayoutType;

  // Output events
  filterChange$ = output<FilterSelection[]>();

  constructor() {
    super();

    effect(() => {
      const selectedGridViewLayoutValue = this.selectedGridViewLayout();
      this._selectedGridViewLayout.set(selectedGridViewLayoutValue);
    });

    effect(() => {
      const categoryLayoutDataValue = this.categoryLayoutData();
      this._categoryLayoutData.set(categoryLayoutDataValue);
    });
  }

  ngOnInit(): void {
    this.applyCategoryThemeStyle();
  }

  ngAfterViewInit(): void {
    // Check for click actions after view is initialized
    this.checkForClickActions();

    // Use DOM Mutation Observer instead of setTimeout for better reliability
    this.domMutationObserver.watchForClickActions(
      'category-layout-click-actions',
      this.elementRef,
      (anchorElements) => {
        // Re-check click actions when new anchor elements with click actions are added
        this.checkForClickActions();
      }
    );
  }

  changeGridViewLayout(gridViewLayout: GridViewLayout): void {
    this._selectedGridViewLayout.set(gridViewLayout);
    this.selectedGridViewLayoutChange$.emit(gridViewLayout);
  }

  onSortByChange(sortValue: string) {
    this.sortByChange$.emit(sortValue);
  }

  applyCategoryThemeStyle(): void {
    const categoryThemeContainer = this.document?.getElementsByClassName("dynamic-listing-page-theme")[0];

    if (this.categoryLayoutData()?.categoryLayoutConfig?.themeBackgroundColor && categoryThemeContainer) {
      this.renderer.setStyle(categoryThemeContainer, 'background-color', this.categoryLayoutData().categoryLayoutConfig.themeBackgroundColor);
    }

    if (this.categoryLayoutData()?.categoryLayoutConfig?.themeFontFamily && categoryThemeContainer) {
      this.renderer.setStyle(categoryThemeContainer, 'font-family', this.categoryLayoutData().categoryLayoutConfig.themeFontFamily);
    }
  }

  ngOnDestroy(): void {
    // Clean up DOM mutation observer
    this.domMutationObserver.disconnect('category-layout-click-actions');
    this.destroy();
  }
}

